import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import {
	organisationRelationShowQueryKey,
	roleEnum,
	updateOrganisationAccessModeDataAccessMode,
	useOrganisationRelationShow,
	useOrganisationUpdateAccessMode,
} from '@jobilla/nexus-sdk'
import type { UpdateOrganisationAccessModeDataAccessMode } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorErrorBoundary } from '../../../../editor/error-handling'
import { ConfirmationDialog, type ConfirmationDialogRef, emitUnhandledError } from '../../../../rapid-ui'
import { SettingsSubheading } from '../../shared'

export function OrganizationAccess() {
	return (
		<RequireAccess role={[roleEnum.admin, roleEnum.jobillan]}>
			<EditorErrorBoundary debugId="settings-organization-access">
				<AccessSection />
			</EditorErrorBoundary>
		</RequireAccess>
	)
}

function AccessSection() {
	const { t } = useTranslation('common')

	return (
		<>
			<SettingsSubheading>{t('organization.access.heading')}</SettingsSubheading>
			<AccessModePicker />
		</>
	)
}

function AccessModePicker() {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const { data: organisation, isPending: isOrganisationLoading } = useOrganisationRelationShow(organisationId, {
		enabled: !!organisationId,
	})

	const currentMode = (organisation?.accessMode as UpdateOrganisationAccessModeDataAccessMode | null | undefined)
		?? null

	const { mutateAsync: updateAccessMode, isPending } = useOrganisationUpdateAccessMode()

	const [showCards, setShowCards] = useState(currentMode !== null)
	const [justSavedMode, setJustSavedMode] = useState<UpdateOrganisationAccessModeDataAccessMode | null>(null)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	useEffect(() => {
		if (currentMode !== null && !showCards) {
			setShowCards(true)
		}
	}, [currentMode, showCards])

	useEffect(() => {
		return () => {
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
		}
	}, [])

	const apply = async (mode: UpdateOrganisationAccessModeDataAccessMode | null) => {
		if (!organisationId) { return }
		try {
			await updateAccessMode({
				organisation: organisationId,
				data: { accessMode: mode },
			})
			await queryClient.invalidateQueries({
				queryKey: organisationRelationShowQueryKey(organisationId),
			})
			if (mode === null) {
				setShowCards(false)
			} else {
				setJustSavedMode(mode)
				if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
				savedTimerRef.current = setTimeout(() => setJustSavedMode(null), 2000)
			}
		} catch (err) {
			logger.error(err, {
				tags: { component: 'OrganizationAccess', action: 'update-access-mode' },
			})
			emitUnhandledError({
				title: t('organization.access.errors.updateFailed'),
				description: err instanceof Error ? err.message : undefined,
				cause: err,
			})
		}
	}

	const confirmSwitch = async (mode: UpdateOrganisationAccessModeDataAccessMode) => {
		if (currentMode === mode) { return }

		logger.track(`Access Mode Override: ${mode === 'implicit' ? 'Implicit' : 'Explicit'} confirmation shown`, {
			data: { previous_mode: currentMode },
		})

		try {
			await confirmRef.current?.confirm({
				title: t(`organization.access.confirm.${mode}.title`),
				message: t(`organization.access.confirm.${mode}.message`),
				confirmText: t(`organization.access.confirm.${mode}.confirm`),
				cancelText: t('organization.access.confirm.cancel'),
				destructive: true,
			})
		} catch {
			logger.track(`Access Mode Override: ${mode === 'implicit' ? 'Implicit' : 'Explicit'} cancelled`, {
				data: { previous_mode: currentMode },
			})
			return
		}

		logger.track(`Access Mode Override: ${mode === 'implicit' ? 'Implicit' : 'Explicit'} selected`, {
			data: { previous_mode: currentMode },
		})
		void apply(mode)
	}

	const handleSelectImplicit = () => {
		void confirmSwitch(updateOrganisationAccessModeDataAccessMode.implicit)
	}

	const handleSelectExplicit = () => {
		void confirmSwitch(updateOrganisationAccessModeDataAccessMode.explicit)
	}

	const handleReset = async () => {
		if (currentMode === null) {
			logger.track('Access Mode Override: Reset', {
				data: { previous_mode: currentMode },
			})
			setShowCards(false)
			return
		}

		logger.track('Access Mode Override: Reset confirmation shown', {
			data: { previous_mode: currentMode },
		})

		try {
			await confirmRef.current?.confirm({
				title: t('organization.access.confirm.reset.title'),
				message: t('organization.access.confirm.reset.message'),
				confirmText: t('organization.access.confirm.reset.confirm'),
				cancelText: t('organization.access.confirm.cancel'),
				destructive: true,
			})
		} catch {
			logger.track('Access Mode Override: Reset cancelled', {
				data: { previous_mode: currentMode },
			})
			return
		}

		logger.track('Access Mode Override: Reset', {
			data: { previous_mode: currentMode },
		})
		void apply(null)
	}

	const handleOpenPicker = () => {
		logger.track('Access Mode Override: Picker opened')
		setShowCards(true)
	}

	if (isOrganisationLoading || !organisation) {
		return <AccessModeSkeleton />
	}

	if (currentMode === null && !showCards) {
		return (
			<div className="max-w-[600px] mt-6">
				<div className="rounded-xl border border-grey-200 bg-grey-50/50 p-6 text-center">
					<div className="text-sm font-medium text-grey-900">
						{t('organization.access.planDefault.title')}
					</div>
					<p className="text-sm text-grey-600 mt-1.5 max-w-[420px] mx-auto">
						{t('organization.access.planDefault.description')}
					</p>
					<div className="mt-5 flex justify-center">
						<button
							type="button"
							onClick={handleOpenPicker}
							className="text-sm text-brand-600 hover:text-brand-700 hover:border-brand-300 border border-grey-200 rounded-md px-3 py-1.5 bg-white cursor-pointer transition-colors"
						>
							{t('organization.access.chooseSpecificMode')}
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="max-w-[600px] mt-6 space-y-4">
			<div className="flex items-center justify-end">
				<button
					type="button"
					onClick={() => void handleReset()}
					disabled={isPending}
					className="text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50 cursor-pointer"
				>
					{t('organization.access.resetToDefault')}
				</button>
			</div>

			<div className="flex flex-col gap-3" role="radiogroup" aria-label={t('organization.access.heading')}>
				<AccessModeCard
					mode={updateOrganisationAccessModeDataAccessMode.implicit}
					selected={currentMode === updateOrganisationAccessModeDataAccessMode.implicit}
					justSaved={justSavedMode === updateOrganisationAccessModeDataAccessMode.implicit}
					disabled={isPending}
					onSelect={handleSelectImplicit}
				/>
				<AccessModeCard
					mode={updateOrganisationAccessModeDataAccessMode.explicit}
					selected={currentMode === updateOrganisationAccessModeDataAccessMode.explicit}
					justSaved={justSavedMode === updateOrganisationAccessModeDataAccessMode.explicit}
					disabled={isPending}
					onSelect={handleSelectExplicit}
				/>
			</div>
			<ConfirmationDialog ref={confirmRef} />
		</div>
	)
}

interface AccessModeCardProps {
	mode: UpdateOrganisationAccessModeDataAccessMode
	selected: boolean
	justSaved?: boolean
	disabled?: boolean
	onSelect?: () => void
}

function AccessModeCard({ mode, selected, justSaved = false, disabled = false, onSelect }: AccessModeCardProps) {
	const { t } = useTranslation('common')

	return (
		<button
			type="button"
			role="radio"
			aria-checked={selected}
			onClick={onSelect}
			disabled={disabled || !onSelect}
			className={`relative text-left rounded-lg border p-4 transition-all
				${
				selected
					? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50/30'
					: 'border-gray-200 hover:border-gray-300 bg-white'
			}
				${disabled || !onSelect ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
		>
			<AnimatePresence>
				{justSaved && (
					<motion.span
						key="saved-badge"
						initial={{ opacity: 0, scale: 0.85 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.85 }}
						transition={{ duration: 0.15 }}
						className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 text-xs font-medium px-2 py-0.5 ring-1 ring-green-200"
					>
						<CheckIcon className="size-3" strokeWidth={2.5} />
						{t('organization.access.saved')}
					</motion.span>
				)}
			</AnimatePresence>
			<div className="flex items-start gap-3">
				<span
					className={`mt-0.5 flex size-4 items-center justify-center rounded-full border-2 flex-shrink-0
						${selected ? 'border-brand-500' : 'border-gray-300'}`}
					aria-hidden="true"
				>
					{selected && <span className="size-2 rounded-full bg-brand-500" />}
				</span>
				<div className="flex-1">
					<div className="text-sm font-medium text-grey-900">
						{t(`organization.access.modes.${mode}.title`)}
					</div>
					<div className="text-sm text-grey-600 mt-0.5">
						{t(`organization.access.modes.${mode}.description`)}
					</div>
				</div>
			</div>
		</button>
	)
}

function AccessModeSkeleton() {
	return (
		<div className="max-w-[600px] mt-6">
			<div className="rounded-xl border border-grey-200 bg-grey-50/50 p-6 animate-pulse">
				<div className="h-4 w-48 bg-grey-200 rounded mx-auto" />
				<div className="h-3 w-72 bg-grey-200 rounded mx-auto mt-3" />
				<div className="h-3 w-56 bg-grey-200 rounded mx-auto mt-2" />
				<div className="h-8 w-40 bg-grey-200 rounded mx-auto mt-5" />
			</div>
		</div>
	)
}
